/**
 * 隐私与授权中心（PRIVACY & CONSENT）。
 *
 * 这是本平台合规的核心页面，因此三件事必须做实：
 *   1. 四级授权逐项独立开关，撤回即时生效（PUT /api/consents/:scope），不做"一键全开"的诱导设计；
 *   2. 声明与实现一致：联邦学习写"架构已就绪、节点接入在实施中"，不声称已经实现；
 *   3. 审计完整性如实呈现，链断了就显示断点，不粉饰。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AuditIntegrity, Consent, ConsentScopeKey } from '@hew3/shared';
import { complianceText, consentScopes } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { formatDateTime } from '../../lib/format.ts';
import { useAuth } from '../../store/auth.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Button, Card, StateBox, Switch } from '../../components/ui.tsx';

interface ConsentListResponse {
  items: Consent[];
}

const levelText: Record<number, string> = {
  1: '第 1 级 · 生理医疗',
  2: '第 2 级 · 行为习惯',
  3: '第 3 级 · 企业经营',
  4: '第 4 级 · 财富与保障',
  5: '匿名化科研（独立同意）',
};

export function ConsentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);
  const authConsents = useAuth((s) => s.consents);
  const setConsents = useAuth((s) => s.setConsents);

  // 登录时已经拉过一次；为空（例如刷新后 store 尚未填充）才补一次请求
  const consentsQuery = useQuery({
    queryKey: ['consents'],
    queryFn: () => api.get<ConsentListResponse>('/api/consents'),
    enabled: authConsents.length === 0,
  });

  const integrityQuery = useQuery({
    queryKey: ['consent', 'audit', 'integrity'],
    queryFn: () => api.get<AuditIntegrity>('/api/consent/audit/integrity'),
  });

  const toggleConsent = useMutation({
    mutationFn: (vars: { scope: ConsentScopeKey; granted: boolean; label: string }) =>
      api.put<ConsentListResponse>(`/api/consents/${vars.scope}`, { granted: vars.granted }),
    onSuccess: (data, vars) => {
      setConsents(data.items);
      void queryClient.invalidateQueries({ queryKey: ['consents'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      // 授权变更本身也会写入审计链，完整性读数需要刷新
      void queryClient.invalidateQueries({ queryKey: ['consent', 'audit'] });
      showToast(vars.granted ? `已开启「${vars.label}」授权` : `已撤回「${vars.label}」授权，相关数据调用立即停止`);
    },
    onError: () => {
      showToast('授权状态更新失败，请重试', 'error');
    },
  });

  const items = authConsents.length > 0 ? authConsents : (consentsQuery.data?.items ?? []);
  const integrity = integrityQuery.data;

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          隐私与授权<small>PRIVACY &amp; CONSENT</small>
        </div>
      </div>

      <div className="note-src">
        <b>本页约束的是谁</b>：以下开关约束的是<strong>平台、专家与 AI 对您数据的使用</strong>；
        它<strong>不影响您本人查看自己的数据</strong> —— 撤回任何一项授权后，您依然可以正常查看健康、
        企业与财富三块内容。
      </div>

      {/* ---------------- 四级授权开关 ---------------- */}
      <Card title="四级数据授权" more={<span className="card-more">逐项独立 · 可随时撤回</span>}>
        {authConsents.length === 0 && consentsQuery.isLoading && <StateBox kind="loading" title="正在加载授权状态…" />}
        {authConsents.length === 0 && consentsQuery.isError && (
          <StateBox
            kind="error"
            title="授权状态加载失败"
            desc="为安全起见，加载失败时不展示开关的默认状态。"
            action={<Button small onClick={() => void consentsQuery.refetch()}>重新加载</Button>}
          />
        )}
        {authConsents.length === 0 && !consentsQuery.isLoading && !consentsQuery.isError && items.length === 0 && (
          <StateBox title="暂无授权项" desc="授权范围由平台预置，若长时间为空请联系您的专属管家" />
        )}

        {items.map((item) => {
          const busy = toggleConsent.isPending && toggleConsent.variables?.scope === item.scope;
          // 服务端为兼容小程序仍下发「资产与保障数据」；H5 展示层恢复品牌词。
          const displayLabel = item.label.replaceAll('资产与保障', '财富与保障');
          return (
            <div key={item.scope} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div className="li-main">
                <div className="li-t">{displayLabel}</div>
                <div className="li-s">{item.desc}</div>
                <div className="row-flex" style={{ marginTop: 7, gap: 8, flexWrap: 'wrap' }}>
                  <Badge tone={item.granted ? 'ok' : 'ghost'}>
                    <span style={{ fontSize: 11 }}>{item.granted ? '已授权' : '已撤回'}</span>
                  </Badge>
                  <span className="text-tiny text-muted">{levelText[item.level] ?? `第 ${item.level} 级`}</span>
                </div>
                <div className="text-tiny text-muted" style={{ marginTop: 5 }}>
                  最近变更：{formatDateTime(item.updatedAt)}
                </div>
              </div>
              <Switch
                on={item.granted}
                busy={busy}
                label={`${displayLabel}授权开关`}
                onToggle={() =>
                  toggleConsent.mutate({ scope: item.scope, granted: !item.granted, label: displayLabel })
                }
              />
            </div>
          );
        })}
      </Card>

      {/* ---------------- 撤回的即时效果 ---------------- */}
      <Card title="撤回会发生什么">
        <div className="text-tiny text-muted" style={{ lineHeight: 1.8 }}>
          撤回后相关数据调用<strong>立即停止</strong>：AI 归因链不会再引用该数据域，专家侧查阅权限同步收窄，
          后续任何调用都会被系统拒绝并记录在案。
          <br />
          已经发生的调用记录不会消失 —— 审计链只允许追加，不允许改写。
        </div>
        <div className="sec-note">
          已撤回的授权不会影响历史报告与服务记录的完整性，您本人对这些数据始终可见。
        </div>
      </Card>

      {/* ---------------- 不可篡改审计 ---------------- */}
      <Card
        title="不可篡改审计"
        more={<span className="card-more">哈希链式存储</span>}
      >
        {integrityQuery.isLoading && <StateBox kind="loading" title="正在校验审计链…" />}
        {integrityQuery.isError && (
          <StateBox
            kind="error"
            title="审计完整性校验失败"
            desc="无法确认审计链状态，请稍后重试。"
            action={<Button small onClick={() => void integrityQuery.refetch()}>重新校验</Button>}
          />
        )}
        {integrity && (
          <>
            <div className="row between">
              <span className="text-tiny text-muted">审计链总条数</span>
              <span className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                {integrity.total}
              </span>
            </div>
            <div className="row between" style={{ marginTop: 8 }}>
              <span className="text-tiny text-muted">完整性校验</span>
              {integrity.valid ? (
                <Badge tone="ok">
                  <span style={{ fontSize: 11 }}>链路完整 · 未被篡改</span>
                </Badge>
              ) : (
                <Badge tone="danger">
                  <span style={{ fontSize: 11 }}>链路断裂</span>
                </Badge>
              )}
            </div>
            {!integrity.valid && (
              <div className="note-src" style={{ color: 'var(--danger)', borderLeftColor: 'var(--danger)' }}>
                <b style={{ color: 'var(--danger)' }}>断点记录 ID</b>：{integrity.brokenAt ?? '断点位置未知'}。
                该位置之后的记录无法验证，平台已进入人工复核流程。
              </div>
            )}
          </>
        )}
        <div className="sec-note">
          记录采用哈希链式存储：每条记录的哈希包含前一条的哈希，任何对历史记录的改写都会导致链断裂并在此暴露。
        </div>
      </Card>

      {/* ---------------- 三绝不原则 ---------------- */}
      <Card title="三绝不原则">
        {complianceText.threeNever.map((n) => (
          <div key={n.t} className="list-item" style={{ alignItems: 'flex-start' }}>
            <div className="li-main">
              <div className="li-t">{n.t}</div>
              <div className="li-s">{n.d}</div>
            </div>
          </div>
        ))}
      </Card>

      {/* ---------------- 联邦学习与数据不出域 ---------------- */}
      <Card title="联邦学习与数据不出域">
        <div className="note-src">
          <b>隐私计算架构</b>：平台按"数据不出域、可用不可见"的原则设计 —— 联合建模在您授权的数据域内进行，
          模型只带走参数、不带走原始记录；跨机构协同时优先使用去标识结果与安全聚合。
          <br />
          <b>实施状态（如实说明）</b>：上述架构设计已就绪，
          <b>联邦学习节点接入仍在实施中</b>；在节点上线前，涉及跨机构的数据协同仍以逐次授权 +
          人工复核的方式进行，平台不会以「已实现联邦学习」宣传尚未上线的能力。
        </div>
        <div className="sec-note">
          您可在本页关闭「{consentScopes.find((s) => s.key === 'research')?.label ?? '匿名化科研贡献'}」，
          关闭后您的数据不会被用于任何联合建模。
        </div>
      </Card>

      <div className="sec-note text-center">
        授权变更与数据调用均写入哈希链审计 · 可在「数据调用记录」中逐条查看
      </div>
    </div>
  );
}
